/**
 * Shift+10 刻み（値・時間）の1ステップ先。
 * マルチ Shift+←→ は MULTIPLIER_SHIFT_STEPS を使う（別関数）。
 */

export function getShiftSnapTarget(
  currentValue: number,
  isIncreasing: boolean,
  _options: { clampDecreaseTargetAtZero?: boolean } = {}
): number {
  if (isIncreasing) {
    let targetValue = Math.ceil(currentValue / 10) * 10;
    if (targetValue === currentValue) {
      targetValue = currentValue + 10;
    }
    return targetValue;
  }

  let targetValue = Math.floor(currentValue / 10) * 10;
  if (targetValue === currentValue) {
    targetValue = currentValue - 10;
  }
  return targetValue;
}

/**
 * 呼び出し側: increase → current + delta、decrease → current - delta
 */
export function shiftSnapStepDelta(
  currentValue: number,
  isIncreasing: boolean,
  options: { clampDecreaseTargetAtZero?: boolean } = {}
): number {
  const target = getShiftSnapTarget(currentValue, isIncreasing, options);
  return isIncreasing ? target - currentValue : currentValue - target;
}

/** @deprecated 符号付き delta には shiftSnapStepDelta を使う */
export function shiftSnapIncrementMagnitude(
  currentValue: number,
  isIncreasing: boolean,
  options: { clampDecreaseTargetAtZero?: boolean } = {}
): number {
  return Math.abs(shiftSnapStepDelta(currentValue, isIncreasing, options));
}

/** 増減方向を適用した次の値 */
export function applyShiftSnapStep(
  currentValue: number,
  isIncreasing: boolean,
  options?: { clampDecreaseTargetAtZero?: boolean }
): number {
  return getShiftSnapTarget(currentValue, isIncreasing, options);
}

/** Shift+マルチ ←→ の刻み: /20 /10 1% 10%…90% *1 *10 *20（以降は *10 刻み） */
export const MULTIPLIER_SHIFT_STEPS: readonly number[] = [
  -20, -10,
  0.01, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9,
  1, 10, 20,
];

const multStepEquals = (a: number, b: number): boolean =>
  Math.abs(a - b) < 0.001;

/** Shift+マルチ操作の次の multiplier 値 */
export function getShiftMultiplierTarget(
  currentMultiplier: number,
  isIncreasing: boolean
): number {
  const steps = MULTIPLIER_SHIFT_STEPS;
  const cur = currentMultiplier;

  const exactIdx = steps.findIndex((s) => multStepEquals(s, cur));
  if (exactIdx >= 0) {
    if (isIncreasing) {
      if (exactIdx < steps.length - 1) return steps[exactIdx + 1];
      return steps[exactIdx] + 10;
    }
    if (exactIdx > 0) return steps[exactIdx - 1];
    return steps[exactIdx] - 10;
  }

  if (isIncreasing) {
    // /20 より大きい除算（-50 等）は /10 刻みで 0 方向へ（/50→/40→…→/20）
    if (cur < -20) {
      const decade = Math.ceil(cur / 10) * 10;
      if (cur < decade - 0.001) return decade;
      return cur + 10;
    }
    const next = steps.find((s) => s > cur + 0.001);
    if (next !== undefined) return next;
    if (cur >= 20) {
      const decade = Math.round(cur / 10) * 10;
      return decade <= cur ? decade + 10 : decade;
    }
    return cur + 10;
  }

  // *20 より大きい値は *10 刻み（100→90→…→30→20）。配列の最大 *20 へ飛ばない
  if (cur > 20) {
    const decade = Math.floor(cur / 10) * 10;
    if (cur > decade + 0.001) return decade;
    return cur - 10;
  }

  // /20 より大きい除算は /10 刻みでより除算側へ（/50→/60）
  if (cur < -20) {
    const decade = Math.floor(cur / 10) * 10;
    if (cur > decade + 0.001) return decade;
    return cur - 10;
  }

  const prev = [...steps].reverse().find((s) => s < cur - 0.001);
  if (prev !== undefined) return prev;
  return steps[0] - 10;
}

/** Shift+マルチ用の正の歩幅（呼び出し側は +/- する） */
export function shiftMultiplierStepMagnitude(
  currentMultiplier: number,
  isIncreasing: boolean
): number {
  const target = getShiftMultiplierTarget(currentMultiplier, isIncreasing);
  return Math.abs(target - currentMultiplier);
}

export function applyShiftMultiplierStep(
  currentMultiplier: number,
  isIncreasing: boolean
): number {
  const target = getShiftMultiplierTarget(currentMultiplier, isIncreasing);
  return target;
}
